import React, { useState } from "react";
import { Alert, Button, Collapse, DatePicker, Form, Input, Select, Spin, Table, Tag, Tooltip } from "antd";
import { CheckCircleFilled, CloseCircleFilled, FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchProcurementAuditReportQuery } from "features/store";

const { Panel } = Collapse;

const MATERIAL_TYPES = ["CLOTH MATERIAL", "CAP MATERIAL", "OTHER MATERIAL"];

const HAS_PROOF_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "bill", label: "Bill Only" },
  { value: "challan", label: "Challan Only" },
  { value: "none", label: "None" },
];

const ProofBadge = ({ hasProof, url }) => {
  if (hasProof) {
    return (
      <Tooltip title="View proof">
        <a href={url} target="_blank" rel="noreferrer">
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
        </a>
      </Tooltip>
    );
  }
  return <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />;
};

const csvHeaders = [
  { label: "Material Name", key: "material_name" },
  { label: "Color", key: "color" },
  { label: "Current Qty", key: "current_quantity" },
  { label: "Bill No", key: "bill_number" },
  { label: "Has Bill Proof", key: "has_bill_proof" },
  { label: "Bill Proof URL", key: "bill_proof_url" },
  { label: "Challan No", key: "challan_number" },
  { label: "Has Challan Proof", key: "has_challan_proof" },
  { label: "Challan Proof URL", key: "challan_proof_url" },
  { label: "Last Updated", key: "last_updated" },
  { label: "Created By", key: "created_by" },
];

const InvRepAudit = () => {
  const [form] = Form.useForm();
  const [fieldErrors, setFieldErrors] = useState({});
  const [trigger, { data, isLoading, isFetching, error }] = useLazyFetchProcurementAuditReportQuery();

  const handleApply = async () => {
    setFieldErrors({});
    const v = form.getFieldsValue();
    const params = {};
    if (v.bill_number) params.bill_number = v.bill_number;
    if (v.challan_number) params.challan_number = v.challan_number;
    if (v.date_from) params.date_from = v.date_from.format("YYYY-MM-DD");
    if (v.date_to) params.date_to = v.date_to.format("YYYY-MM-DD");
    if (v.created_from) params.created_from = v.created_from.format("YYYY-MM-DD");
    if (v.created_to) params.created_to = v.created_to.format("YYYY-MM-DD");
    if (v.type) params.type = v.type;
    if (v.has_proof) params.has_proof = v.has_proof;
    if (v.created_by) params.created_by = v.created_by;
    if (v.updated_by) params.updated_by = v.updated_by;
    const result = await trigger(params);
    if (result?.error?.status === 400 && result.error.data?.errors) {
      setFieldErrors(result.error.data.errors);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setFieldErrors({});
    trigger({});
  };

  const results = data?.results || [];
  const count = data?.count ?? results.length;

  const columns = [
    { title: "Material Name", dataIndex: "material_name", key: "material_name" },
    { title: "Color", dataIndex: "color", key: "color" },
    { title: "Current Qty", dataIndex: "current_quantity", key: "current_quantity" },
    { title: "Bill No", dataIndex: "bill_number", key: "bill_number" },
    {
      title: "Bill Proof",
      key: "bill_proof",
      render: (_, r) => <ProofBadge hasProof={r.has_bill_proof} url={r.bill_proof_url} />,
    },
    { title: "Challan No", dataIndex: "challan_number", key: "challan_number" },
    {
      title: "Challan Proof",
      key: "challan_proof",
      render: (_, r) => <ProofBadge hasProof={r.has_challan_proof} url={r.challan_proof_url} />,
    },
    { title: "Last Updated", dataIndex: "last_updated", key: "last_updated" },
    { title: "Created By", dataIndex: "created_by", key: "created_by" },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginBottom: 16 }}>Procurement Audit Report</h2>

      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form form={form} layout="inline" style={{ flexWrap: "wrap", gap: "8px 0" }}>
            <Form.Item name="bill_number" label="Bill No">
              <Input placeholder="Bill number" style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="challan_number" label="Challan No">
              <Input placeholder="Challan number" style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="date_from" label="Date From">
              <DatePicker />
            </Form.Item>
            <Form.Item name="date_to" label="Date To">
              <DatePicker />
            </Form.Item>
            <Form.Item name="type" label="Material Type">
              <Select allowClear placeholder="All types" style={{ width: 180 }}>
                {MATERIAL_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="has_proof" label="Has Proof">
              <Select allowClear placeholder="Any" style={{ width: 140 }}>
                {HAS_PROOF_OPTIONS.map((o) => (
                  <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="created_by" label="Created By">
              <Input placeholder="Username" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="updated_by"
              label="Updated By"
              validateStatus={fieldErrors.updated_by ? "error" : ""}
              help={fieldErrors.updated_by}
            >
              <Input placeholder="Username" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              name="created_from"
              label="Created From"
              validateStatus={fieldErrors.created_from ? "error" : ""}
              help={fieldErrors.created_from}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              name="created_to"
              label="Created To"
              validateStatus={fieldErrors.created_to ? "error" : ""}
              help={fieldErrors.created_to}
            >
              <DatePicker />
            </Form.Item>
          </Form>
          {error?.status === 400 && !error?.data?.errors && (
            <Alert type="error" message={error?.data?.detail || "Invalid filter values."} style={{ marginTop: 8 }} />
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {data && (
          <Tag color="blue" style={{ fontSize: 14 }}>Total Records: {count}</Tag>
        )}
        {results.length > 0 && (
          <Tooltip title="Export to Excel">
            <CSVLink
              data={results}
              headers={csvHeaders}
              filename="procurement-audit-report.csv"
            >
              <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                Export to Excel
              </Button>
            </CSVLink>
          </Tooltip>
        )}
      </div>

      <Spin spinning={isLoading || isFetching}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(r, i) => r.id ?? i}
          locale={{ emptyText: "No records found" }}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default InvRepAudit;
